<template>
  <div ref="hotContainer" class="handsontable-container"></div>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.min.css';

export default {
  name: 'CsvTableEditor',
  props: {
    modelValue: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const hotContainer = ref(null);
    let hotInstance = null;

    // 解析CSV字符串为二维数组
    const parseCSV = (csvString) => {
      if (!csvString || !csvString.trim()) {
        return [[]];
      }

      const lines = csvString.split('\n').filter(line => line.trim() || line.includes(','));
      if (lines.length === 0) {
        return [[]];
      }

      // 简单的CSV解析函数，支持引号包裹的字段
      const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          
          if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
              // 转义的引号
              current += '"';
              i++;
            } else {
              // 切换引号状态
              inQuotes = !inQuotes;
            }
          } else if (char === ',' && !inQuotes) {
            // 字段分隔符
            result.push(current);
            current = '';
          } else {
            current += char;
          }
        }
        
        // 添加最后一个字段
        result.push(current);
        return result;
      };

      return lines.map(line => parseCSVLine(line));
    };

    // 将二维数组转换为CSV字符串
    const convertToCSV = (data) => {
      if (!data || data.length === 0) {
        return '';
      }

      // 格式化CSV字段值
      const formatCSVField = (value) => {
        if (value === null || value === undefined) {
          return '';
        }
        const str = String(value);
        // 如果值包含逗号、引号或换行符，需要用引号包裹
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      return data.map(row => {
        return row.map(cell => formatCSVField(cell)).join(',');
      }).join('\n');
    };

    // 初始化Handsontable
    const initHandsontable = () => {
      if (!hotContainer.value) return;

      const data = parseCSV(props.modelValue || '');
      
      // 如果数据为空，创建默认的空表格
      const tableData = data.length === 0 || (data.length === 1 && data[0].length === 0) 
        ? [['']] 
        : data;

      hotInstance = new Handsontable(hotContainer.value, {
        data: tableData,
        rowHeaders: true,
        colHeaders: true,
        contextMenu: false,
        manualColumnResize: true,
        manualRowResize: true,
        columnSorting: true,
        filters: true,
        dropdownMenu: true,
        licenseKey: 'non-commercial-and-evaluation', // 非商业用途许可证
        height: 400,
        width: '100%',
        stretchH: 'all',
        autoWrapRow: true,
        autoWrapCol: true,
        afterChange: (changes, source) => {
          if (source !== 'loadData') {
            const csv = convertToCSV(hotInstance.getData());
            emit('update:modelValue', csv);
            emit('change', csv);
          }
        },
        afterCreateRow: (index, amount) => {
          const csv = convertToCSV(hotInstance.getData());
          emit('update:modelValue', csv);
          emit('change', csv);
        },
        afterCreateCol: (index, amount) => {
          const csv = convertToCSV(hotInstance.getData());
          emit('update:modelValue', csv);
          emit('change', csv);
        },
        afterRemoveRow: () => {
          const csv = convertToCSV(hotInstance.getData());
          emit('update:modelValue', csv);
          emit('change', csv);
        },
        afterRemoveCol: () => {
          const csv = convertToCSV(hotInstance.getData());
          emit('update:modelValue', csv);
          emit('change', csv);
        }
      });
    };


    // 监听外部值变化
    watch(() => props.modelValue, (newValue) => {
      if (hotInstance) {
        const currentCSV = convertToCSV(hotInstance.getData());
        if (newValue !== currentCSV) {
          const data = parseCSV(newValue || '');
          hotInstance.loadData(data.length > 0 && data[0].length > 0 ? data : [['']]);
        }
      }
    }, { immediate: false });

    onMounted(() => {
      nextTick(() => {
        initHandsontable();
      });
    });

    onBeforeUnmount(() => {
      if (hotInstance) {
        hotInstance.destroy();
        hotInstance = null;
      }
    });

    return {
      hotContainer
    };
  }
};
</script>

<style scoped>
.handsontable-container {
  width: 100%;
}
</style>
